import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';
import { PAYMENT_STATUS } from '../services/paymentService';

const AgentDashboard = ({ user, userData }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [referredUsers, setReferredUsers] = useState([]);

  const [stats, setStats] = useState({
    totalReferred: 0,
    jobSeekers: 0,
    employers: 0,
    totalRevenue: 0,
    totalCommission: 0
  });

  const [referralLink, setReferralLink] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (!user || !userData) return;

    const fetchReferredUsers = async () => {
      try {
        const link = `https://harshaljobs.com/register?ref=${user.uid}`;
        setReferralLink(link);

        const usersRef = collection(db, 'users');

        const q = query(usersRef, where('referredBy', '==', user.uid));

        const querySnapshot = await getDocs(q);

        const commissionRate = userData?.commissionRate ?? 10;

        const referralsWithPayments = await Promise.all(
          querySnapshot.docs.map(async (docSnap) => {
            const referralData = {
              id: docSnap.id,
              ...docSnap.data()
            };

            const paymentsQuery = query(
              collection(db, 'payments'),
              where('userId', '==', docSnap.id),
              where('status', '==', PAYMENT_STATUS.SUCCESS)
            );

            const paymentsSnap = await getDocs(paymentsQuery);

            const totalRevenue = paymentsSnap.docs.reduce((sum, paymentDoc) => {
              const payment = paymentDoc.data();

              return sum + (payment.totalAmount || payment.amount || 0);
            }, 0);

            const commission = (totalRevenue * commissionRate) / 100;

            return {
              ...referralData,
              totalRevenue,
              commission,
              paymentsCount: paymentsSnap.size
            };
          })
        );

        let seekerCount = 0;
        let employerCount = 0;
        let totalRevenue = 0;
        let totalCommission = 0;

        referralsWithPayments.forEach((referral) => {
          totalRevenue += referral.totalRevenue;
          totalCommission += referral.commission;

          if (referral.userType === 'jobseeker') {
            seekerCount++;
          } else if (referral.userType === 'employer') {
            employerCount++;
          }
        });

        setReferredUsers(referralsWithPayments);

        setStats({
          totalReferred: referralsWithPayments.length,
          jobSeekers: seekerCount,
          employers: employerCount,
          totalRevenue,
          totalCommission
        });

        setLoading(false);
      } catch (error) {
        console.error('Error fetching referred users:', error);
        setLoading(false);
      }
    };

    fetchReferredUsers();
  }, [user, userData]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);

    setCopySuccess(true);

    setTimeout(() => {
      setCopySuccess(false);
    }, 2000);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 p-6">

        <div className="max-w-7xl mx-auto">

          {/* Header */}

          <div className="mb-10">

            <h1 className="text-4xl font-extrabold text-gray-900">
              Agent Dashboard
            </h1>

            <p className="text-gray-600 mt-2 text-lg">
              Welcome back, {userData?.firstName} {userData?.lastName}
            </p>
          </div>

          {/* ===================== STATS CARDS ===================== */}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10">

            {/* Total Referred */}

            <div className="relative overflow-hidden rounded-3xl bg-white shadow-xl border border-white/40 p-6 group hover:-translate-y-2 transition duration-300">

              <div className="absolute -top-10 -right-10 w-36 h-36 bg-blue-500/10 rounded-full"></div>

              <div className="flex justify-between items-start mb-8 relative z-10">

                <div>

                  <p className="text-sm font-medium text-gray-500">
                    Total Referred
                  </p>

                  <h2 className="text-4xl font-black text-gray-900 mt-2">
                    {stats.totalReferred}
                  </h2>
                </div>

                <div className="w-14 h-14 rounded-2xl bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/30">

                  <svg
                    className="w-7 h-7 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0"
                    />
                  </svg>
                </div>
              </div>

              <div className="relative z-10">

                <div className="flex justify-between items-center">

                  <span className="text-sm text-gray-500">
                    All Users
                  </span>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                  <div className="bg-blue-500 h-2 rounded-full w-4/5"></div>
                </div>
              </div>
            </div>

            {/* Job Seekers */}

            <div className="relative overflow-hidden rounded-3xl bg-white shadow-xl border border-white/40 p-6 group hover:-translate-y-2 transition duration-300">

              <div className="absolute -bottom-10 -left-10 w-36 h-36 bg-green-500/10 rounded-full"></div>

              <div className="flex justify-between items-start mb-8 relative z-10">

                <div>

                  <p className="text-sm font-medium text-gray-500">
                    Job Seekers
                  </p>

                  <h2 className="text-4xl font-black text-gray-900 mt-2">
                    {stats.jobSeekers}
                  </h2>
                </div>

                <div className="w-14 h-14 rounded-2xl bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/30">

                  <svg
                    className="w-7 h-7 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745"
                    />
                  </svg>
                </div>
              </div>

              <div className="relative z-10">

                <div className="flex justify-between items-center">

                  <span className="text-sm text-gray-500">
                    Looking For Jobs
                  </span>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                  <div className="bg-green-500 h-2 rounded-full w-3/4"></div>
                </div>
              </div>
            </div>

            {/* Employers */}

            <div className="relative overflow-hidden rounded-3xl bg-white shadow-xl border border-white/40 p-6 group hover:-translate-y-2 transition duration-300">

              <div className="absolute -top-10 left-0 w-36 h-36 bg-purple-500/10 rounded-full"></div>

              <div className="flex justify-between items-start mb-8 relative z-10">

                <div>

                  <p className="text-sm font-medium text-gray-500">
                    Employers
                  </p>

                  <h2 className="text-4xl font-black text-gray-900 mt-2">
                    {stats.employers}
                  </h2>
                </div>

                <div className="w-14 h-14 rounded-2xl bg-purple-500 flex items-center justify-center shadow-lg shadow-purple-500/30">

                  <svg
                    className="w-7 h-7 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7"
                    />
                  </svg>
                </div>
              </div>

              <div className="relative z-10">

                <div className="flex justify-between items-center">

                  <span className="text-sm text-gray-500">
                    Hiring Talent
                  </span>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                  <div className="bg-purple-500 h-2 rounded-full w-2/3"></div>
                </div>
              </div>
            </div>

            {/* Commission */}

            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-500 to-red-500 shadow-2xl p-6 text-white group hover:-translate-y-2 transition duration-300">

              <div className="absolute top-0 right-0 w-44 h-44 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>

              <div className="flex justify-between items-start mb-8 relative z-10">

                <div>

                  <p className="text-sm font-medium text-orange-100">
                    Total Commission
                  </p>

                  <h2 className="text-4xl font-black mt-2">
                    ₹{stats.totalCommission.toLocaleString()}
                  </h2>
                </div>

                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">

                  <svg
                    className="w-7 h-7 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2"
                    />
                  </svg>
                </div>
              </div>

              <div className="relative z-10">

                <div className="flex justify-between items-center">

                  <span className="text-sm text-orange-100">
                    Commission Rate
                  </span>

                  <span className="text-sm font-bold">
                    {userData?.commissionRate || 10}%
                  </span>
                </div>

                <div className="w-full bg-white/20 rounded-full h-2 mt-3">
                  <div className="bg-white h-2 rounded-full w-5/6"></div>
                </div>
              </div>
            </div>
          </div>

          {/* ===================== REVENUE SECTION ===================== */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">

            {/* Total Revenue */}

            <div className="bg-white rounded-3xl shadow-xl p-6 border border-gray-100">

              <div className="flex justify-between items-center mb-6">

                <div>

                  <p className="text-gray-500 text-sm">
                    Total Revenue
                  </p>

                  <h2 className="text-4xl font-black text-gray-900 mt-2">
                    ₹{stats.totalRevenue.toLocaleString()}
                  </h2>
                </div>

                <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center">

                  <svg
                    className="w-8 h-8 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2"
                    />
                  </svg>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-2xl p-4">

                <div className="flex justify-between">

                  <span className="text-sm text-gray-600">
                    Avg Revenue/User
                  </span>

                  <span className="font-bold text-emerald-700">
                    ₹
                    {stats.totalReferred > 0
                      ? (
                          stats.totalRevenue / stats.totalReferred
                        ).toLocaleString()
                      : 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Pending Payout */}

            <div className="bg-white rounded-3xl shadow-xl p-6 border border-gray-100">

              <div className="flex justify-between items-center mb-6">

                <div>

                  <p className="text-gray-500 text-sm">
                    Total Commission
                  </p>

                  <h2 className="text-4xl font-black text-gray-900 mt-2">
                    ₹{stats.totalCommission.toLocaleString()}
                  </h2>
                </div>

                <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center">

                  <svg
                    className="w-8 h-8 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 14l2 2 4-4"
                    />
                  </svg>
                </div>
              </div>

              <div className="bg-orange-50 rounded-2xl p-4">

                <div className="flex justify-between">

                  <span className="text-sm text-gray-600">
                    Pending Payout
                  </span>

                  <span className="font-bold text-orange-700">
                    ₹0
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ===================== REFERRAL LINK ===================== */}

          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 rounded-3xl shadow-2xl p-6 mb-10 overflow-hidden relative">

            <div className="absolute top-0 right-0 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>

            <div className="flex items-center gap-4 mb-5 relative z-10">

              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center">

                <svg
                  className="w-7 h-7 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0"
                  />
                </svg>
              </div>

              <div>

                <h2 className="text-3xl font-bold text-white">
                  Referral Link
                </h2>

                <p className="text-indigo-100">
                  Share your link & earn commission
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 relative z-10">

              <input
                type="text"
                value={referralLink}
                readOnly
                className="flex-1 px-5 py-4 rounded-2xl bg-white/20 border border-white/30 text-white outline-none backdrop-blur-md"
              />

              <button
                onClick={copyToClipboard}
                className={`px-8 py-4 rounded-2xl font-bold transition duration-300 shadow-xl ${
                  copySuccess
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-yellow-400 hover:bg-yellow-500 text-black'
                }`}
              >
                {copySuccess ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>

          {/* ===================== TABLE ===================== */}

          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">

            <div className="p-6 border-b border-gray-100">

              <h2 className="text-2xl font-bold text-gray-900">
                Referred Users
              </h2>
            </div>

            {referredUsers.length > 0 ? (
              <div className="overflow-x-auto">

                <table className="w-full">

                  <thead className="bg-gray-50">

                    <tr>

                      <th className="text-left py-4 px-6 text-sm font-bold text-gray-700">
                        User
                      </th>

                      <th className="text-left py-4 px-6 text-sm font-bold text-gray-700">
                        Type
                      </th>

                      <th className="text-left py-4 px-6 text-sm font-bold text-gray-700">
                        Revenue
                      </th>

                      <th className="text-left py-4 px-6 text-sm font-bold text-gray-700">
                        Commission
                      </th>

                      <th className="text-left py-4 px-6 text-sm font-bold text-gray-700">
                        Joined
                      </th>
                    </tr>
                  </thead>

                  <tbody>

                    {referredUsers.map((refUser) => (
                      <tr
                        key={refUser.id}
                        className="border-b border-gray-100 hover:bg-blue-50 transition"
                      >

                        <td className="py-5 px-6">

                          <div className="flex items-center gap-4">

                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                              {refUser.firstName?.charAt(0)?.toUpperCase()}
                            </div>

                            <div>

                              <p className="font-bold text-gray-900">
                                {refUser.firstName} {refUser.lastName}
                              </p>

                              <p className="text-sm text-gray-500">
                                {refUser.email}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="py-5 px-6">

                          <span
                            className={`px-4 py-2 rounded-full text-xs font-bold ${
                              refUser.userType === 'jobseeker'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-purple-100 text-purple-700'
                            }`}
                          >
                            {refUser.userType === 'jobseeker'
                              ? 'Job Seeker'
                              : 'Employer'}
                          </span>
                        </td>

                        <td className="py-5 px-6 font-semibold text-gray-800">
                          ₹{refUser.totalRevenue.toLocaleString()}
                        </td>

                        <td className="py-5 px-6 font-bold text-orange-600">
                          ₹{refUser.commission.toLocaleString()}
                        </td>

                        <td className="py-5 px-6 text-gray-600">
                          {refUser.createdAt?.toDate?.()?.toLocaleDateString() ||
                            'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16">

                <div className="text-7xl mb-4">
                  👥
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  No Referred Users
                </h3>

                <p className="text-gray-500">
                  Share your referral link to get started
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default AgentDashboard;